import { useId, useState, type ReactNode } from 'react';
import {
  MOBILITY_RIDE_NOTES_MAX,
  type MobilityRideDetailsBlock,
  type MobilityRideDetailsSubmit,
} from '@voll-intelligence/genui-contract';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Input } from '@voll-intelligence/ui-kit/ui/input';
import { Label } from '@voll-intelligence/ui-kit/ui/label';
import { Textarea } from '@voll-intelligence/ui-kit/ui/textarea';
import { BLOCK_SHELL } from '../../../shared/ui/block_shell.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { maskPhone, phoneE164 } from '../../shared/phone/phone_mask.js';

const HELP = 'm-0 text-caption text-muted-foreground';
const LABEL = 'text-ui font-medium text-foreground';

/** O registro do card respondido (ou superado): o que a pessoa deixou, sem campo. */
function RideDetailsRecord({
  phoneLabel,
  phone,
  notes,
}: Readonly<{ phoneLabel: string; phone: string; notes: string }>): ReactNode {
  const { t } = useGenUiI18n();
  return (
    <dl className="m-0 grid gap-2 text-ui">
      <div className="grid gap-0.5">
        <dt className="text-caption text-muted-foreground">{phoneLabel}</dt>
        <dd className="m-0 text-foreground">{phone || '—'}</dd>
      </div>
      {notes && (
        <div className="grid gap-0.5">
          <dt className="text-caption text-muted-foreground">
            {t('productCheckout.mobility.notes')}
          </dt>
          <dd className="m-0 text-foreground">{notes}</dd>
        </div>
      )}
    </dl>
  );
}

/**
 * Os detalhes da corrida na conversa (`mobility_ride_details`, #6699): o
 * telefone do passageiro, obrigatório e só desta corrida — nunca vai para o
 * perfil de quem viaja —, e as observações para o motorista, opcionais. O
 * Continuar só libera com um número que dá para ligar (com DDD no BR); o erro
 * aparece depois que a pessoa sai do campo, ligado por `aria-describedby`. No
 * celular os campos já empilham e o Continuar ocupa a largura toda.
 */
export function MobilityRideDetailsCard({
  ui,
  note,
  readOnly = false,
  superseded = false,
  onSubmit,
}: Readonly<{
  ui: MobilityRideDetailsBlock;
  note?: string;
  readOnly?: boolean;
  superseded?: boolean;
  onSubmit: (submit: MobilityRideDetailsSubmit) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const ids = useId();
  const [phone, setPhone] = useState(() => (ui.phone ? maskPhone(ui.phone) : ''));
  const [notes, setNotes] = useState(ui.notes ?? '');
  const [touched, setTouched] = useState(false);
  const name = ui.travelerName;
  const phoneLabel = name
    ? t('mobilityRideDetails.phoneOf', { name })
    : t('mobilityRideDetails.phoneSelf');
  const e164 = phoneE164(phone);
  const invalid = touched && phone !== '' && e164 === null;
  const helpId = `${ids}-help`;
  const errorId = `${ids}-error`;
  const notesHelpId = `${ids}-notes-help`;
  const locked = readOnly || superseded;
  const submit = () => {
    if (!e164) return;
    const trimmed = notes.trim();
    onSubmit({
      action: 'mobility_ride_details_submit',
      phone: e164,
      ...(trimmed ? { notes: trimmed } : {}),
    });
  };
  return (
    <div className="genui2 grid gap-3">
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      <section
        aria-label={t('mobilityRideDetails.title')}
        className={`${BLOCK_SHELL} @container gap-4`}
      >
        {locked ? (
          <RideDetailsRecord phoneLabel={phoneLabel} phone={phone} notes={notes.trim()} />
        ) : (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              setTouched(true);
              submit();
            }}
          >
            <div className="grid gap-1.5">
              <Label htmlFor={`${ids}-phone`} className={LABEL}>
                {phoneLabel}
              </Label>
              <Input
                id={`${ids}-phone`}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                aria-required="true"
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? `${errorId} ${helpId}` : helpId}
                placeholder={t('mobilityRideDetails.phonePlaceholder')}
                value={phone}
                className="pointer-coarse:h-11"
                onChange={(event) => {
                  setPhone(maskPhone(event.target.value));
                }}
                onBlur={() => {
                  setTouched(true);
                }}
              />
              {invalid && (
                <p id={errorId} className="m-0 text-caption font-medium text-destructive">
                  {t('mobilityRideDetails.phoneError')}
                </p>
              )}
              <p id={helpId} className={HELP}>
                {name
                  ? t('mobilityRideDetails.phoneHelpOf', { name })
                  : t('mobilityRideDetails.phoneHelpSelf')}
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${ids}-notes`} className={LABEL}>
                {t('mobilityRideDetails.notes')}
              </Label>
              <Textarea
                id={`${ids}-notes`}
                aria-describedby={notesHelpId}
                maxLength={MOBILITY_RIDE_NOTES_MAX}
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
              />
              <p id={notesHelpId} className={HELP}>
                {t('mobilityRideDetails.notesHelp')}
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={e164 === null}
                className="@max-[theme(--breakpoint-sm)]:h-11 @max-[theme(--breakpoint-sm)]:w-full"
              >
                {t('mobilityRideDetails.continue')}
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
